/**
 * Visitor ID management for analytics tracking.
 *
 * Uses a first-party cookie to maintain consistent visitor identification
 * across sessions for analytics purposes.
 */

import { getCookie, setCookie } from '../utils/cookies';

/**
 * Cookie name for visitor ID.
 */
export const VISITOR_COOKIE_NAME = 'gp_popups_vid';

/**
 * Cookie duration in days.
 */
export const VISITOR_COOKIE_DAYS = 365;

/**
 * Generate a UUID v4.
 *
 * @return A random UUID string.
 */
function generateUUID(): string {
	// Use crypto.randomUUID if available (modern browsers)
	if (typeof crypto !== 'undefined' && crypto.randomUUID) {
		return crypto.randomUUID();
	}

	// Fallback for older browsers
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
		/[xy]/g,
		function (c) {
			// eslint-disable-next-line no-bitwise
			const r = Math.trunc(Math.random() * 16);
			// eslint-disable-next-line no-bitwise
			const v = c === 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		}
	);
}

/**
 * Validate that a string looks like a UUID.
 *
 * @param value - The value to validate.
 * @return True if the value looks like a UUID.
 */
function isValidUUID(value: string): boolean {
	return /^[a-f0-9-]{36}$/.test(value);
}

/**
 * Get or create a visitor ID.
 *
 * Checks for an existing visitor ID cookie, validates it,
 * and creates a new one if necessary.
 *
 * @return The visitor ID.
 */
export function getOrCreateVisitorId(): string {
	// Check for existing cookie
	const existingId = getCookie(VISITOR_COOKIE_NAME);

	if (existingId && isValidUUID(existingId)) {
		return existingId;
	}

	// Generate new visitor ID
	const visitorId = generateUUID();

	// Store in cookie
	setCookie(VISITOR_COOKIE_NAME, visitorId, VISITOR_COOKIE_DAYS);

	return visitorId;
}

/**
 * Get the current visitor ID without creating one.
 *
 * @return The visitor ID or null if not set.
 */
export function getVisitorId(): string | null {
	const existingId = getCookie(VISITOR_COOKIE_NAME);

	if (existingId && isValidUUID(existingId)) {
		return existingId;
	}

	return null;
}
